import { useState, useEffect, useCallback } from "react";
import { Search, ChevronDown, ChevronRight, BookOpen, Loader2, Inbox } from "lucide-react";
import DOMPurify from "dompurify";
import { helpApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Group articles by section
function groupBySection(articles) {
  return articles.reduce((acc, a) => {
    if (!acc[a.section]) acc[a.section] = [];
    acc[a.section].push(a);
    return acc;
  }, {});
}

function ArticleCard({ article, isOpen, onToggle }) {
  return (
    <div className="border border-slate-200 bg-white" data-testid={`help-article-${article.article_id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors text-left gap-4"
      >
        <span className="text-sm font-medium text-slate-800">{article.title}</span>
        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {isOpen && (
        <div
          className="px-5 pb-5 pt-1 prose prose-sm max-w-none prose-headings:text-slate-800 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:text-cyan-700 prose-pre:bg-slate-950 prose-pre:text-slate-200 border-t border-slate-100"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content || "") }}
        />
      )}
    </div>
  );
}

export default function Help() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(new Set());

  const portalType = ["admin", "super_admin"].includes(user?.role) ? "admin" : "customer";

  const fetchArticles = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const params = { portal_type: portalType, language_code: "en" };
      if (q.trim()) params.q = q.trim();
      const data = await helpApi.listArticles(params);
      setArticles(Array.isArray(data) ? data : []);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [portalType]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchArticles(query), 350);
    return () => clearTimeout(t);
  }, [query, fetchArticles]);

  const toggle = (id) => setOpenIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const sections = groupBySection(articles);
  const sectionNames = Object.keys(sections).sort();

  return (
    <div className="p-6 space-y-6" data-testid="help-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="w-5 h-5 text-cyan-600" />
          <h1 className="text-2xl font-bold text-slate-900">Help & Support</h1>
        </div>
        <p className="text-sm text-slate-500">Browse documentation and guides for the QCoreSecure platform.</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search help articles…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 bg-white text-sm focus:outline-none focus:border-cyan-500 transition-colors"
          data-testid="help-search-input"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading articles…
        </div>
      ) : articles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3" data-testid="help-empty-state">
          <Inbox className="w-10 h-10" />
          <p className="text-sm font-medium">No help articles available yet.</p>
          {query && <p className="text-xs">Try a different search term.</p>}
        </div>
      ) : (
        <div className="space-y-8">
          {sectionNames.map((section) => (
            <div key={section}>
              <h2 className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3 px-1">{section}</h2>
              <div className="space-y-px">
                {sections[section].map((article) => (
                  <ArticleCard
                    key={article.article_id}
                    article={article}
                    isOpen={openIds.has(article.article_id)}
                    onToggle={() => toggle(article.article_id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
